<template>
  <div>
    <div :class="`c-sidemenu ${props.isOpen ? '' : 'c-sidemenu--closed'}`">
      <div class="c-sidemenu__header">
        <button type="button" class="c-sidemenu__close" aria-label="閉じる" @click="emit('close')">
          <IconXLine :size="24" :color="CLOSE_ICON_COLOR" />
        </button>
      </div>
      <ul class="c-sidemenu__list">
        <li v-for="item in MENU_ITEMS" :key="item.path" :class="item.danger ? 'c-sidemenu__item--danger' : ''">
          <a @click="emit('select', item.path)">{{ item.text }}</a>
        </li>
      </ul>
    </div>
    <div v-if="props.isOpen" class="c-sidemenu__overlay" @click="emit('close')" />
  </div>
</template>

<script lang="ts" setup>
import '~/styles/_global.scss'
import '~/styles/troodon/_variables.scss'
import { watch, onBeforeUnmount } from 'vue'
import IconXLine from '~/components/icon/x-line.vue'
import { setBodyModalLock } from '~/utils/body-modal-lock'

const CLOSE_ICON_COLOR = '#878d96'

interface MenuItem {
  text: string
  path: string
  danger?: boolean
}

// メニューから外れた会員情報系はアカウント(/me/account/)配下から到達する。
const MENU_ITEMS: MenuItem[] = [
  { text: '予約', path: '/me/booking/' },
  { text: '会計', path: '/me/order/request/' },
  { text: '診察券', path: '/me/patient-card/' },
  { text: 'サブスク', path: '/me/subscription/' },
  { text: 'グループ管理', path: '/me/group/' },
  { text: 'アカウント', path: '/me/account/' },
  { text: 'ログアウト', path: '/sign-out/', danger: true },
]

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'select', path: string): void
}>()

// ドロワーは全画面オーバーレイで背面を覆うため、開いている間は TroodonModal 同様に背面(body)を
// ロックしスクロールバー幅を補償する(useModal を通らない自前 isOpen 駆動のため setBodyModalLock を直接呼ぶ)。
watch(() => props.isOpen, setBodyModalLock, { immediate: true })

// 開いたままアンマウントされてもロックが残らないようにする。
onBeforeUnmount(() => {
  if (props.isOpen) setBodyModalLock(false)
})
</script>

<style lang="scss">
.c-sidemenu {
  background-color: var(--troodon-c-white);
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: 280px;
  max-width: 90vw;
  z-index: 90;
  display: flex;
  flex-direction: column;
  transition: right 250ms ease;

  a {
    cursor: pointer;
  }

  &.c-sidemenu--closed {
    right: -400px;
  }

  .c-sidemenu__header {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: flex-end;
    padding: 16px;
  }

  .c-sidemenu__close {
    display: flex;
    width: 24px;
    height: 24px;
    padding: 0;
    border: 0;
    background-color: transparent;
    cursor: pointer;
  }

  .c-sidemenu__list {
    flex: 1;
    overflow: auto;
    padding: 0;
    margin: 0;
    list-style: none;

    li {
      display: flex;
      align-items: center;
      padding: 16px 24px;
      border-bottom: 1px solid var(--troodon-c-border-low);
      font-size: var(--troodon-f-size-md);
      font-weight: 700;
      line-height: var(--troodon-lh-body);

      a {
        flex: 1 0 0;
        color: var(--troodon-c-text-high);
        text-decoration: none;
      }
    }

    .c-sidemenu__item--danger a {
      color: var(--troodon-c-error);
    }
  }
}

.c-sidemenu__overlay {
  display: block;
  position: fixed;
  left: 0;
  top: 0;
  width: 100vw;
  height: 100vh;
  z-index: 89;
  background-color: rgba(0, 0, 0, 0.3);
}

@media (max-width: 600px) {
  body.has-globalnav .c-sidemenu {
    height: calc(100vh - 89px);
  }
}
</style>
